"use client"

type AudioKey =
  | "intro"
  | "introWave"
  | "underwater"
  | "bulBul"
  | "musicStem"
  | "drone"
  | "hose"
  | "vacuum"
  | "net"
  | "win"
  | "uiClick"
  | "uiText"
  | "qrScan"

type AudioConfig = {
  src: string
  volume: number
  loop?: boolean
}

const AUDIO_CONFIG: Record<AudioKey, AudioConfig> = {
  intro: { src: "/audio/intro.mp3", volume: 0.42 },
  introWave: { src: "/audio/intro-wave.mp3", volume: 0.32, loop: true },
  underwater: { src: "/audio/underwater.mp3", volume: 0.48, loop: true },
  bulBul: { src: "/audio/bul%20bul.mp3", volume: 0.28, loop: true },
  musicStem: { src: "/audio/music-stem.mp3", volume: 0.2, loop: true },
  drone: { src: "/audio/drone.mp3", volume: 0.11, loop: true },
  hose: { src: "/audio/hose.mp3", volume: 0.22, loop: true },
  vacuum: { src: "/audio/vacuum.mp3", volume: 0.18 },
  net: { src: "/audio/net.mp3", volume: 0.36 },
  win: { src: "/audio/win.mp3", volume: 0.48 },
  uiClick: { src: "/audio/ui-click.mp3", volume: 0.22 },
  uiText: { src: "/audio/ui-text.mp3", volume: 0.36 },
  qrScan: { src: "/audio/qr-scan.mp3", volume: 0.18 },
}

const GAME_WEAPON_AUDIO: Record<string, AudioKey> = {
  beluga: "vacuum",
  blueTang: "vacuum",
  neons: "vacuum",
  octopus: "vacuum",
  stingray: "net",
  turtle: "net",
  whale: "net",
}

const GAME_LOOP_AUDIO_KEYS: AudioKey[] = ["underwater", "bulBul"]
const INTRO_LOOP_KEYS: AudioKey[] = ["introWave", "musicStem"]
const SKIMMER_LOOP_AUDIO_KEYS: AudioKey[] = ["drone"]

const audioElements: Partial<Record<AudioKey, HTMLAudioElement>> = {}
let activeGameId: string | null = null
let gamePaused = false
let introAudioActive = false
let lifecycleListenersAttached = false
let pageAudioSuspended = false
const lifecyclePausedAudioKeys = new Set<AudioKey>()

function canUseAudio() {
  return typeof window !== "undefined" && typeof Audio !== "undefined"
}

function isDocumentHidden() {
  return typeof document !== "undefined" && document.visibilityState !== "visible"
}

function suspendPageAudio() {
  if (!canUseAudio()) return

  pageAudioSuspended = true
  ;(Object.keys(audioElements) as AudioKey[]).forEach((key) => {
    const audio = audioElements[key]
    if (!audio || audio.paused || audio.ended) return

    lifecyclePausedAudioKeys.add(key)
    audio.pause()
  })
}

function shouldRetryResume(key: AudioKey) {
  if (introAudioActive && INTRO_LOOP_KEYS.includes(key)) return true
  if (!activeGameId) return false
  if (GAME_LOOP_AUDIO_KEYS.includes(key)) return true
  return activeGameId === "beluga" && SKIMMER_LOOP_AUDIO_KEYS.includes(key)
}

function resumePageAudio() {
  if (!canUseAudio() || isDocumentHidden()) return

  pageAudioSuspended = false
  const keysToResume = Array.from(lifecyclePausedAudioKeys)
  lifecyclePausedAudioKeys.clear()

  keysToResume.forEach((key) => {
    const audio = audioElements[key]
    if (!audio || audio.ended) return

    const config = AUDIO_CONFIG[key]
    audio.volume = config.volume
    audio.loop = Boolean(config.loop)
    void audio.play().catch(() => {
      if (shouldRetryResume(key)) {
        lifecyclePausedAudioKeys.add(key)
      }
    })
  })

  if (introAudioActive) {
    INTRO_LOOP_KEYS.forEach((key) => playAudio(key, false))
  }

  if (activeGameId) {
    GAME_LOOP_AUDIO_KEYS.forEach((key) => playAudio(key, false))
    if (activeGameId === "beluga") {
      SKIMMER_LOOP_AUDIO_KEYS.forEach((key) => playAudio(key, false))
    }
    updateGameLoopVolumes()
  }
}

function attachPageAudioLifecycleListeners() {
  if (!canUseAudio() || lifecycleListenersAttached) return

  lifecycleListenersAttached = true

  document.addEventListener("visibilitychange", () => {
    if (isDocumentHidden()) {
      suspendPageAudio()
      return
    }

    resumePageAudio()
  })

  window.addEventListener("pagehide", suspendPageAudio)
  window.addEventListener("pageshow", resumePageAudio)
  window.addEventListener("blur", suspendPageAudio)
  window.addEventListener("focus", resumePageAudio)
  document.addEventListener("click", resumePageAudio, { capture: true })
  document.addEventListener("pointerdown", resumePageAudio, { capture: true })
  document.addEventListener("touchstart", resumePageAudio, {
    capture: true,
    passive: true,
  })

  if (isDocumentHidden()) {
    suspendPageAudio()
  }
}

function getAudio(key: AudioKey) {
  if (!canUseAudio()) return null
  attachPageAudioLifecycleListeners()

  const existing = audioElements[key]
  if (existing) return existing

  const config = AUDIO_CONFIG[key]
  const audio = new Audio(config.src)
  audio.preload = "auto"
  audio.loop = Boolean(config.loop)
  audio.volume = config.volume
  audioElements[key] = audio
  return audio
}

function playAudio(key: AudioKey, restart = true) {
  if (isDocumentHidden()) {
    suspendPageAudio()
    return
  }

  if (pageAudioSuspended) {
    resumePageAudio()
    if (pageAudioSuspended) return
  }

  const audio = getAudio(key)
  if (!audio) return

  const config = AUDIO_CONFIG[key]
  audio.volume = config.volume
  audio.loop = Boolean(config.loop)
  if (restart) {
    audio.currentTime = 0
  }

  void audio.play().catch(() => {})
}

function stopAudio(key: AudioKey) {
  const audio = audioElements[key]
  lifecyclePausedAudioKeys.delete(key)
  if (!audio) return

  audio.pause()
  audio.currentTime = 0
}

function updateGameLoopVolumes() {
  GAME_LOOP_AUDIO_KEYS.forEach((key) => {
    const audio = getAudio(key)
    if (!audio) return
    audio.volume = AUDIO_CONFIG[key].volume
  })

  SKIMMER_LOOP_AUDIO_KEYS.forEach((key) => {
    const audio = getAudio(key)
    if (!audio) return
    const baseVolume = AUDIO_CONFIG[key].volume
    audio.volume = gamePaused ? baseVolume * 0.45 : baseVolume
  })
}

export function preloadGameAudio() {
  if (!canUseAudio()) return
  attachPageAudioLifecycleListeners()

  ;(Object.keys(AUDIO_CONFIG) as AudioKey[]).forEach((key) => {
    getAudio(key)?.load()
  })
}

export function unlockGameAudio() {
  preloadGameAudio()
}

export function playUiClickSound() {
  playAudio("uiClick")
}

export function playUiTextSound() {
  playAudio("uiText")
}

export function startIntroAudio() {
  preloadGameAudio()
  introAudioActive = true
  INTRO_LOOP_KEYS.forEach((key) => playAudio(key, false))
}

export function stopIntroAudio() {
  introAudioActive = false
  stopAudio("intro")
  INTRO_LOOP_KEYS.forEach(stopAudio)
}

export function startGameAudio(gameId: string) {
  preloadGameAudio()
  stopIntroAudio()
  activeGameId = gameId
  gamePaused = false

  GAME_LOOP_AUDIO_KEYS.forEach((key) => playAudio(key, false))
  if (gameId === "beluga") {
    SKIMMER_LOOP_AUDIO_KEYS.forEach((key) => playAudio(key, false))
  } else {
    SKIMMER_LOOP_AUDIO_KEYS.forEach(stopAudio)
  }
  updateGameLoopVolumes()
}

export function stopGameAudio() {
  activeGameId = null
  GAME_LOOP_AUDIO_KEYS.forEach(stopAudio)
  SKIMMER_LOOP_AUDIO_KEYS.forEach(stopAudio)
  stopAudio("vacuum")
}

export function setGameAudioPaused(paused: boolean) {
  gamePaused = paused
  if (activeGameId) {
    GAME_LOOP_AUDIO_KEYS.forEach((key) => playAudio(key, false))
  }
  updateGameLoopVolumes()
}

export function playWeaponCollectSound(gameId = activeGameId) {
  if (!gameId) return

  const key = GAME_WEAPON_AUDIO[gameId]
  if (!key) return

  playAudio(key)
}

export function startWeaponUseSound(gameId = activeGameId) {
  if (!gameId || gamePaused) return

  const key = GAME_WEAPON_AUDIO[gameId]
  if (key !== "vacuum") return

  playAudio(key)
}

export function stopWeaponUseSound(gameId = activeGameId) {
  if (!gameId) return

  const key = GAME_WEAPON_AUDIO[gameId]
  if (key !== "vacuum") return

  stopAudio(key)
}

export function startTubeFlowSound() {
  playAudio("hose", false)
}

export function stopTubeFlowSound() {
  stopAudio("hose")
}

export function playWinSound() {
  stopGameAudio()
  playAudio("win")
}

export function playQrScanSound() {
  playAudio("qrScan")
}

export function isGameAudioPageSuspended() {
  return pageAudioSuspended || isDocumentHidden()
}
